"use client";

import React, { useState, useEffect } from "react";
import { useAudioProcessing } from "@/hooks/useAudioProcessing";
import { Report } from "@/types";
import { getFormats, type Format } from "@/lib/clients/formats";

// AudioUploadFormの通常インポート
import { AudioUploadForm } from "./AudioUploadForm";

import { ProcessingStatusDisplay } from "./ProcessingStatusDisplay";
import ReportDisplay from "./ReportDisplay";

export type UploadStatus = {
  state: "PENDING" | "SAVING" | "SUCCESS" | "ERROR";
  message: string;
};

type DynamoLikeString = {
  S?: string;
  [key: string]: unknown;
};

export type ProcessingStatus = {
  userId?: string;
  reportId?: string;
  employeeID?: string;
  reportID?: string;
  states: string;
  structure_definition_id?: string;
  report?: (Report & { [key: string]: unknown }) | {
    pk?: string | DynamoLikeString;
    sk?: string | DynamoLikeString;
    meeting_data?: string | DynamoLikeString | Record<string, unknown>;
    transcribedText?: string | DynamoLikeString;
    transcribedtext?: string | DynamoLikeString;
    structure_definition_id?: string | DynamoLikeString;
    formatid?: string | DynamoLikeString;
    custompromptid?: string | DynamoLikeString;
    customprompttext?: string | DynamoLikeString;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
};

const FormProcessDisplays = () => {
  const { 
    status, 
    audioUrl, 
    fileName, 
    processingStatus, 
    isSubmitting,
    isRebuilding,
    onSubmit,
    rebuildReport
  } = useAudioProcessing();
  
  // アニメーションの状態管理
  const [showReport, setShowReport] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // フォーマットリストの状態
  const [formats, setFormats] = useState<Format[]>([]);
  const [isFormatsLoading, setIsFormatsLoading] = useState<boolean>(false);

  useEffect(() => {
    // フォーマットの取得を遅延実行
    const loadFormats = async () => {
      try {
        console.log('フォーマットを取得中...');
        setIsFormatsLoading(true);
        const formatList = await getFormats();
        console.log('取得されたフォーマット:', formatList);
        
        const mappedFormats = formatList.map((format) => ({
          id: format.id,
          name: format.name,
          format: format.format,
        }));
        
        console.log('マッピング後のフォーマット:', mappedFormats);
        setFormats(mappedFormats);
      } catch (error) {
        console.error('Failed to load formats:', error);
        setFormats([]);
      } finally {
        setIsFormatsLoading(false);
      }
    };

    // RequestIdleCallback で遅延実行
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadFormats, { timeout: 200 });
    } else {
      setTimeout(loadFormats, 100);
    }
  }, []);

  // processingStatusの変化に応じてアニメーション状態を更新
  useEffect(() => {
    if (processingStatus?.report && processingStatus?.states === "reqCompleted") {
      setIsTransitioning(true);
      // フェードアウトのアニメーション時間を確保
      setTimeout(() => {
        setShowReport(true);
        setIsTransitioning(false);
      }, 300);
    } else {
      setShowReport(false);
    }
  }, [processingStatus]);

  return (
    <section className="w-full max-w-3xl min-w-96 mx-auto pointer-events-auto min-h-screen flex flex-col justify-start">
      <div className="md:mt-18 mt-2 mb-12 px-2 md:px-20 lg:px-0 flex-grow">
        {showReport && processingStatus?.report && processingStatus?.states === "reqCompleted" ? (
          // レポートが存在し、状態が完了している場合はReportDisplayを表示
          <div key="report-display" className={`fade-scale-in transition-component ${isTransitioning ? 'transition-component-enter' : 'transition-component-enter-active'}`}>
            <ReportDisplay 
              processingStatus={processingStatus} 
              rebuildReport={rebuildReport}
              isRebuilding={isRebuilding}
            />
          </div>
        ) : (
          // 処理中またはレポートがない場合はアップロードフォームとステータス表示
          <div 
            key="upload-form" 
            className={`transition-component ${isTransitioning ? 'fade-scale-out transition-component-exit-active' : 'transition-component-enter-active'}`}
          >
            <div>
              <AudioUploadForm 
                onSubmit={onSubmit}
                isSubmitting={isSubmitting} 
                status={status}
                audioUrl={audioUrl}
                fileName={fileName}
                formats={formats}
                isFormatsLoading={isFormatsLoading}
              />
            </div>
            
            {(processingStatus || status.state !== "PENDING") && (
              <div key="processing-status">
                <ProcessingStatusDisplay 
                  processingStatus={processingStatus}
                  uploadStatus={status}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default FormProcessDisplays;
